import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callApi } from './api';
import { AdminWithdrawal, LeaderboardEntry, ReferralAuditAction, ReferralAuditEntry, ReferralSettings, ReferredUser } from '@/models/referral';

const adminUrl = import.meta.env.VITE_API_URL_V1 + '/admin/referrals';

/* ---------------- Settings ---------------- */
export const getReferralSettings = async () => {
    return callApi<ReferralSettings>({ url: `${adminUrl}/settings`, method: 'GET' });
};

export const useReferralSettings = () => {
    return useQuery({ queryKey: ['admin', 'referrals', 'settings'], queryFn: getReferralSettings });
};

export const updateReferralSettings = async (data: Partial<ReferralSettings>) => {
    return callApi<ReferralSettings>({ url: `${adminUrl}/settings`, method: 'PUT', data });
};

export const useUpdateReferralSettings = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: updateReferralSettings,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'referrals'] }),
    });
};

/* ---------------- Leaderboard ---------------- */
export const getReferralsLeaderboard = async (limit = 25) => {
    return callApi<{ users: LeaderboardEntry[] }>({ url: `${adminUrl}/leaderboard?limit=${limit}`, method: 'GET' });
};

export const useReferralsLeaderboard = (limit = 25) => {
    return useQuery({
        queryKey: ['admin', 'referrals', 'leaderboard', limit],
        queryFn: () => getReferralsLeaderboard(limit),
    });
};

/* ---------------- Referred users ---------------- */
export interface ReferredUsersParams {
    status?: string;
    cursor?: string;
    limit?: number;
    search?: string;
}

export const getReferredUsers = async (params: ReferredUsersParams = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.search) qs.set('search', params.search);
    const url = `${adminUrl}/referred-users${qs.toString() ? `?${qs.toString()}` : ''}`;
    return callApi<{ referredUsers: ReferredUser[]; nextCursor: string | null }>({ url, method: 'GET' });
};

export const useReferredUsers = (params: ReferredUsersParams = {}) => {
    return useQuery({
        queryKey: ['admin', 'referred-users', params.status, params.cursor, params.search],
        queryFn: () => getReferredUsers(params),
    });
};

/* ---------------- Withdrawals ---------------- */
export const getAdminWithdrawals = async (status?: string, limit = 50) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    qs.set('limit', String(limit));
    return callApi<{ withdrawals: AdminWithdrawal[] }>({ url: `${adminUrl}/withdrawals?${qs.toString()}`, method: 'GET' });
};

export const useAdminWithdrawals = (status?: string, limit = 50) => {
    return useQuery({
        queryKey: ['admin', 'referrals', 'withdrawals', status, limit],
        queryFn: () => getAdminWithdrawals(status, limit),
    });
};

/* ---------------- Audit log ---------------- */
export const getReferralAuditLog = async (params: { action?: ReferralAuditAction; targetUserId?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.action) qs.set('action', params.action);
    if (params.targetUserId) qs.set('targetUserId', params.targetUserId);
    if (params.limit) qs.set('limit', String(params.limit));
    const url = `${adminUrl}/audit-log${qs.toString() ? `?${qs.toString()}` : ''}`;
    return callApi<{ entries: ReferralAuditEntry[] }>({ url, method: 'GET' });
};

export const useReferralAuditLog = (params: { action?: ReferralAuditAction; targetUserId?: string; limit?: number } = {}) => {
    return useQuery({
        queryKey: ['admin', 'referrals', 'audit-log', params.action, params.targetUserId, params.limit],
        queryFn: () => getReferralAuditLog(params),
    });
};

/* ---------------- Per-user actions ---------------- */
export const overrideUserTier = async ({ userId, tier, reason }: { userId: string; tier: 1 | 2 | 3 | null; reason?: string }) => {
    return callApi({
        url: `${adminUrl}/users/${userId}/tier`,
        method: 'PUT',
        data: { tier, reason },
    });
};

export const useOverrideUserTier = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: overrideUserTier,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'referrals'] }),
    });
};

export const flagUser = async ({ userId, flagged, reason }: { userId: string; flagged: boolean; reason?: string }) => {
    return callApi({
        url: `${adminUrl}/users/${userId}/flag`,
        method: 'PUT',
        data: { flagged, reason },
    });
};

export const useFlagUser = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: flagUser,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'referrals'] }),
    });
};

export const adjustUserBalance = async ({ userId, deltaCents, reason }: { userId: string; deltaCents: number; reason: string }) => {
    return callApi({
        url: `${adminUrl}/users/${userId}/balance`,
        method: 'PUT',
        data: { deltaCents, reason },
    });
};

export const useAdjustUserBalance = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: adjustUserBalance,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'referrals'] }),
    });
};

/* ---------------- Approve / reject withdrawal (admin) ---------------- */
/**
 * Marks a Requested withdrawal as Completed.
 *
 * Manual mode (current — Stripe Connect Express deferred): admin has sent
 * the payment out-of-band and supplies a `reference` (e.g. Wise tx id) shown
 * to the user in the "withdrawal sent" email. `reason` is for the audit log.
 *
 * TODO(stripe-connect): once Connect resumes, `reference` becomes optional —
 * the Stripe transfer id is the de-facto reference.
 */
export const approveWithdrawal = async ({
    userId,
    withdrawalId,
    reference,
    reason,
}: {
    userId: string;
    withdrawalId: string;
    reference?: string;
    reason?: string;
}) => {
    const data: { reference?: string; reason?: string } = {};
    if (reference) data.reference = reference;
    if (reason) data.reason = reason;
    return callApi({
        url: `${adminUrl}/withdrawals/${userId}/${withdrawalId}/approve`,
        method: 'PUT',
        data,
    });
};

export const useApproveWithdrawal = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: approveWithdrawal,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'referrals'] }),
    });
};

export const rejectWithdrawal = async ({ userId, withdrawalId, reason }: { userId: string; withdrawalId: string; reason?: string }) => {
    return callApi({
        url: `${adminUrl}/withdrawals/${userId}/${withdrawalId}/reject`,
        method: 'PUT',
        data: reason ? { reason } : {},
    });
};

export const useRejectWithdrawal = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: rejectWithdrawal,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'referrals'] }),
    });
};

/* ---------------- System-wide stats ---------------- */
export interface SystemStats {
    totalReferrals: number;
    qualifiedReferrals: number;
    pendingReferrals: number;
    churnedReferrals: number;
    tierDistribution: { tier1: number; tier2: number; tier3: number };
    commissionGenerated: { pendingCents: number; availableCents: number; lifetimeCents: number };
    funnel: { signedUp: number; purchased: number; qualified: number };
    activeReferrers: number;
}

export const getSystemStats = async () => {
    return callApi<SystemStats>({ url: `${adminUrl}/stats`, method: 'GET' });
};

export const useSystemStats = () => {
    return useQuery({ queryKey: ['admin', 'referrals', 'stats'], queryFn: getSystemStats });
};

/* ---------------- User search ---------------- */
export interface SearchUserResult {
    id: string;
    email: string;
    name?: string;
    referralCode?: string;
    referralTier?: 1 | 2 | 3;
    qualifiedReferralsCount?: number;
    totalReferralsCount?: number;
    referralFlagged?: boolean;
}

export const searchReferralUsers = async (q: string) => {
    const qs = new URLSearchParams({ q });
    return callApi<{ users: SearchUserResult[] }>({ url: `${adminUrl}/search?${qs.toString()}`, method: 'GET' });
};

export const useSearchReferralUsers = (q: string) => {
    return useQuery({
        queryKey: ['admin', 'referrals', 'search', q],
        queryFn: () => searchReferralUsers(q),
        enabled: q.trim().length > 0,
    });
};

export const formatCents = (cents: number, currency: string = 'USD'): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format((cents || 0) / 100);
